import api from '../../../app/api/axios-api';

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export type AnalyzedUploadState = {
  uploadIds: Set<string>;
  baseTitles: Set<string>;
  fileKeys: Set<string>;
};

const addUploadId = (value: unknown, into: Set<string>): void => {
  if (typeof value !== 'string' || !value.trim()) return;
  into.add(value.trim().toLowerCase());
};

export const normalizeUploadBaseTitle = (title: unknown): string => {
  if (typeof title !== 'string') return '';
  return title.replace(UUID_REGEX, '').trim().toLowerCase();
};

const addFileKey = (value: unknown, into: Set<string>): void => {
  if (typeof value !== 'string' || !value.trim()) return;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'api_direct_submission') return;
  into.add(normalized);
};

const extractUploadIdsFromTitle = (title: unknown, into: Set<string>, baseTitles?: Set<string>): void => {
  if (typeof title !== 'string') return;
  for (const match of title.matchAll(UUID_REGEX)) {
    addUploadId(match[0], into);
  }
  if (baseTitles) {
    const base = normalizeUploadBaseTitle(title);
    if (base) baseTitles.add(base);
  }
};

/** Walk feature result trees and collect upload_ids / titles / files already in version history. */
const walkResultNode = (node: unknown, state: AnalyzedUploadState, depth = 0): void => {
  if (node == null || depth > 12) return;

  if (Array.isArray(node)) {
    node.forEach(item => walkResultNode(item, state, depth + 1));
    return;
  }

  if (typeof node !== 'object') return;

  const row = node as Record<string, unknown>;
  addUploadId(row.upload_id, state.uploadIds);
  extractUploadIdsFromTitle(row.title, state.uploadIds, state.baseTitles);
  addFileKey(row.file_url, state.fileKeys);
  addFileKey(row.file_name, state.fileKeys);

  const meta = row.model_metadata;
  if (meta && typeof meta === 'object') {
    const metaRow = meta as Record<string, unknown>;
    addFileKey(metaRow.file_url, state.fileKeys);
    addFileKey(metaRow.file_name, state.fileKeys);
  }

  if (Array.isArray(row.data)) {
    row.data.forEach(item => walkResultNode(item, state, depth + 1));
  }
  if (Array.isArray(row.entries)) {
    row.entries.forEach(item => walkResultNode(item, state, depth + 1));
  }

  const nestedKeys = [
    'bias',
    'bias_llm',
    'fairness',
    'fairness_llm',
    'fairness_metric',
    'explainability',
    'explainability_llm',
    'prompt_injection',
    'ai_safety',
    'ai_safety_llm',
    'abuse_ai',
    'abuse_ai_llm',
    'LLM_Bias_Detection',
    'ML_Bias_Detection',
    'results'
  ];
  for (const key of nestedKeys) {
    if (key in row) {
      walkResultNode(row[key], state, depth + 1);
    }
  }
};

export const collectAnalyzedUploadState = (projectData: Record<string, unknown> | undefined): AnalyzedUploadState => {
  const state: AnalyzedUploadState = {
    uploadIds: new Set<string>(),
    baseTitles: new Set<string>(),
    fileKeys: new Set<string>()
  };
  if (!projectData) return state;

  walkResultNode(projectData.collection_results, state);
  walkResultNode(projectData.feature_results, state);
  walkResultNode(projectData.results, state);

  return state;
};

/** @deprecated Use collectAnalyzedUploadState */
export const collectAnalyzedUploadIds = (projectData: Record<string, unknown> | undefined): Set<string> =>
  collectAnalyzedUploadState(projectData).uploadIds;

/** Always refresh results on Complete so incremental skip sees the latest version history. */
export const mergeProjectResultsIntoProjectData = async (
  projectId: string,
  projectData: Record<string, unknown>
): Promise<Record<string, unknown>> => {
  try {
    const response = await api.get(`/project/features/${projectId}/results`);
    const payload = response.data;
    if (!payload || typeof payload !== 'object') {
      return projectData;
    }

    const merged: Record<string, unknown> = { ...projectData };
    const data = payload as Record<string, unknown>;

    if (data.collection_results != null) merged.collection_results = data.collection_results;
    if (data.feature_results != null) merged.feature_results = data.feature_results;
    if (data.results != null) merged.results = data.results;

    return merged;
  } catch (error) {
    console.warn('Could not load existing project results for incremental analysis:', error);
    return projectData;
  }
};

export const resolveUploadRecordId = (record: any): string | null => {
  if (!record || typeof record !== 'object') return null;
  if (record.upload_id) return String(record.upload_id).toLowerCase();

  const title = record.title || record.tag || '';
  if (typeof title === 'string') {
    const match = title.match(UUID_REGEX);
    if (match?.[0]) return match[0].toLowerCase();
  }

  return null;
};

const resolveUploadFileKey = (record: any): string | null => {
  if (!record || typeof record !== 'object') return null;
  const candidates = [
    record.file_url,
    record.file_name,
    record.model_metadata?.file_url,
    record.model_metadata?.file_name
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() && candidate !== 'api_direct_submission') {
      return candidate.trim().toLowerCase();
    }
  }
  return null;
};

export const isUploadAlreadyAnalyzed = (record: any, state: AnalyzedUploadState | Set<string>): boolean => {
  const analyzed =
    state instanceof Set ? { uploadIds: state, baseTitles: new Set<string>(), fileKeys: new Set<string>() } : state;

  const id = resolveUploadRecordId(record);
  if (id && analyzed.uploadIds.has(id)) return true;

  const baseTitle = normalizeUploadBaseTitle(record?.title || record?.tag);
  if (baseTitle && analyzed.baseTitles.has(baseTitle)) return true;

  const fileKey = resolveUploadFileKey(record);
  if (fileKey && analyzed.fileKeys.has(fileKey)) return true;

  return false;
};

export const isLlmUploadGroupAlreadyAnalyzed = (
  group: { llmModel: any; title: string; entries: any[] },
  state: AnalyzedUploadState
): boolean => {
  if (isUploadAlreadyAnalyzed(group.llmModel, state)) return true;

  for (const entry of group.entries) {
    if (isUploadAlreadyAnalyzed(entry, state)) return true;
  }

  const groupBaseTitle = normalizeUploadBaseTitle(group.title);
  if (groupBaseTitle && state.baseTitles.has(groupBaseTitle)) return true;

  return false;
};

export const filterPendingUploads = <T>(records: T[], state: AnalyzedUploadState, getRecord: (item: T) => any): T[] =>
  records.filter(item => !isUploadAlreadyAnalyzed(getRecord(item), state));
